import React, {useState} from 'react';
import {
    Badge,
    Button,
    Input,
    InputGroup,
    Modal,
    ModalBody,
    ModalHeader,
    Spinner
} from 'reactstrap';
import {Plug} from 'lucide-react';
import {toast} from 'react-toastify';
import {
    ToolAuthConnectionStatus,
    openAuthPopup,
    useDisconnectToolAuth,
    useProvideToolAuthKey,
    useStartToolAuth,
    useToolAuthStatus
} from '../api/toolAuthHooks';

interface ToolAuthButtonProps {
    assistantKeyOrId: string;
}

/**
 * The connections button in the composer area: shown only when the assistant has MCP servers
 * with managed authentication, with a red attention dot while this user still needs to act.
 * Opens the modal where users authenticate, provide keys or disconnect.
 */
const ToolAuthButton: React.FC<ToolAuthButtonProps> = ({assistantKeyOrId}) => {
    const statusQuery = useToolAuthStatus(assistantKeyOrId);
    const startAuth = useStartToolAuth(assistantKeyOrId);
    const provideKey = useProvideToolAuthKey(assistantKeyOrId);
    const disconnect = useDisconnectToolAuth(assistantKeyOrId);

    const [isOpen, setIsOpen] = useState(false);
    const [keyDrafts, setKeyDrafts] = useState<Record<string, string>>({});
    const [authenticatingId, setAuthenticatingId] = useState<string | null>(
        null
    );

    const status = statusQuery.data;
    if (!status?.hasToolsToConfigure) {
        return null;
    }

    const handleAuthenticate = async (connectionId: string) => {
        setAuthenticatingId(connectionId);
        try {
            const {url} = await startAuth.mutateAsync(connectionId);
            await openAuthPopup(url);
            await statusQuery.refetch();
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to start the authorisation'
            );
        } finally {
            setAuthenticatingId(null);
        }
    };

    const handleProvideKey = async (connectionId: string) => {
        const key = (keyDrafts[connectionId] ?? '').trim();
        if (!key) {
            return;
        }

        try {
            await provideKey.mutateAsync({connectionId, key});
            setKeyDrafts(drafts => ({...drafts, [connectionId]: ''}));
            toast.success('Key saved');
        } catch (error) {
            toast.error(
                error instanceof Error ? error.message : 'Failed to save key'
            );
        }
    };

    const handleDisconnect = async (connection: ToolAuthConnectionStatus) => {
        try {
            await disconnect.mutateAsync(connection.connectionId);
            toast.success(`Disconnected from ${connection.name}`);
        } catch (error) {
            toast.error(
                error instanceof Error ? error.message : 'Failed to disconnect'
            );
        }
    };

    const statusBadge = (connection: ToolAuthConnectionStatus) => {
        if (connection.mode === 'staticAdmin') {
            return connection.isAuthenticated ? (
                <Badge color="info">Admin managed</Badge>
            ) : (
                <Badge color="secondary">Not configured</Badge>
            );
        }

        if (connection.mode === 'oauthShared') {
            return connection.isAuthenticated ? (
                <Badge color="info">Shared account</Badge>
            ) : (
                <Badge color="secondary">Not configured</Badge>
            );
        }

        if (connection.isExpired) {
            return <Badge color="warning">Expired</Badge>;
        }

        return connection.isAuthenticated ? (
            <Badge color="success">Connected</Badge>
        ) : (
            <Badge color="secondary">Not connected</Badge>
        );
    };

    const actions = (connection: ToolAuthConnectionStatus) => {
        switch (connection.mode) {
            case 'oauthPerUser':
                return (
                    <div className="d-flex gap-1">
                        {(!connection.isAuthenticated ||
                            connection.isExpired) && (
                            <Button
                                size="sm"
                                color="primary"
                                disabled={
                                    authenticatingId === connection.connectionId
                                }
                                onClick={() =>
                                    void handleAuthenticate(
                                        connection.connectionId
                                    )
                                }
                            >
                                {authenticatingId ===
                                    connection.connectionId && (
                                    <Spinner size="sm" className="me-1" />
                                )}
                                {connection.isExpired
                                    ? 'Re-authenticate'
                                    : 'Authenticate'}
                            </Button>
                        )}
                        {(connection.isAuthenticated ||
                            connection.isExpired) && (
                            <Button
                                size="sm"
                                color="secondary"
                                outline
                                onClick={() =>
                                    void handleDisconnect(connection)
                                }
                            >
                                Disconnect
                            </Button>
                        )}
                    </div>
                );

            case 'staticPerUser':
                return connection.isAuthenticated ? (
                    <Button
                        size="sm"
                        color="secondary"
                        outline
                        onClick={() => void handleDisconnect(connection)}
                    >
                        Clear key
                    </Button>
                ) : (
                    <InputGroup size="sm">
                        <Input
                            type="password"
                            autoComplete="new-password"
                            placeholder="Paste your API key"
                            value={keyDrafts[connection.connectionId] ?? ''}
                            onChange={event =>
                                setKeyDrafts(drafts => ({
                                    ...drafts,
                                    [connection.connectionId]:
                                        event.target.value
                                }))
                            }
                        />
                        <Button
                            color="primary"
                            disabled={
                                !(
                                    keyDrafts[connection.connectionId] ?? ''
                                ).trim() || provideKey.isPending
                            }
                            onClick={() =>
                                void handleProvideKey(connection.connectionId)
                            }
                        >
                            Save
                        </Button>
                    </InputGroup>
                );

            default:
                // Shared OAuth and admin-managed keys: nothing for an end user to do.
                return null;
        }
    };

    return (
        <>
            <Button
                color="link"
                size="sm"
                className="text-muted p-0 position-relative"
                title="Tool connections"
                onClick={() => setIsOpen(true)}
            >
                <Plug size={16} />
                {status.requiresUserAction && (
                    <span
                        className="position-absolute top-0 start-100 translate-middle p-1 bg-danger rounded-circle"
                        style={{marginLeft: -2}}
                    >
                        <span className="visually-hidden">
                            Authentication required
                        </span>
                    </span>
                )}
            </Button>

            <Modal
                isOpen={isOpen}
                toggle={() => setIsOpen(false)}
                onOpened={() => void statusQuery.refetch()}
            >
                <ModalHeader toggle={() => setIsOpen(false)}>
                    Tool connections
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted small">
                        Some of this assistant's tools connect to external
                        services on your behalf.
                    </p>
                    {status.connections.map(connection => (
                        <div
                            key={connection.connectionId}
                            className="border rounded p-2 mb-2"
                        >
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <span className="fw-bold">
                                        {connection.name}
                                    </span>{' '}
                                    {statusBadge(connection)}
                                    {connection.description && (
                                        <div className="text-muted small">
                                            {connection.description}
                                        </div>
                                    )}
                                    <div className="text-muted small">
                                        Used by:{' '}
                                        {connection.serverNames.join(', ')}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-2">{actions(connection)}</div>
                        </div>
                    ))}
                </ModalBody>
            </Modal>
        </>
    );
};

export default ToolAuthButton;
